import React, { useMemo, useState, useEffect, useContext } from "react";
import { shallowEqual, useIntl, useSelector } from "umi";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/components/OverallTableCo/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useMetricsColumns
} from "./hooks";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { MetricsSettingsProp, TableProps } from "./types";
import { useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { SortBy } from "tea-component/lib/table/addons";
import { PagingQuery } from "tea-component";
import { Option } from "@/pages/dashboard/components/DownloadFormatModal/types";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import moment from "moment";
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { LANGUAGE_EN } from "@home/_config/config";
import { getLanguage } from "@home/utils/common";
import { dynamicString } from "@home/utils/dynamicString";


export default function OverallTableCo(props: TableProps) {
  const { config, className, style, data } = props || {};
  const { tableMetric } = data || {};
  const { componentMetrics: configComponentMetrics, titleEn, titleCn, showStatistic: propsShowStatistic, slotConfig, valueTypeConfig, dimensionConfig, otherParams,} = config || {};
  const { isShowValueType = true, isShowMetricsSettings = true, isShowProgressBar = true, isShowdownloadConfig = true } = slotConfig || {};
  const { valueType = "value_only", isShowDod = true, isShowSdlw = true, isShowChange = true } = valueTypeConfig || {};
  const { dateDisabled = true } = dimensionConfig || {};
  
  // 根据data中的tableMetric去过滤config中的componentMetrics
  const componentMetrics = useMemo(() => {
    if(!tableMetric?.length) return configComponentMetrics;
    // 过滤config中的componentMetrics,直接覆盖config中的componentMetrics
    config.componentMetrics = configComponentMetrics?.filter(item => tableMetric?.includes(item?.metricKey));
    return configComponentMetrics?.filter(item => tableMetric?.includes(item?.metricKey));
  },[configComponentMetrics, tableMetric]);

  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const newTitle = getLanguage(isEn, titleEn, titleCn);
  const title = newTitle ? newTitle : formatMessage({ id: "overview_Overall_Performance" });

  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const explainInfo = useSelector((state: any) =>  state.dashboard?.explainInfo, shallowEqual);
  const [columns, setColumns] = useState<any[]>([]);
  // 列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const COLUMN_CONFIG = useMetricsColumns(componentMetrics || [], data || {});

  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));
  const [selectType, setSelectType] = useState<ShowValueType>(valueType || "value_only");
  const [showStatistic, setShowStatistic] = useState<string[]>(propsShowStatistic || defaultStatisticsTypeValue);
  const [accumulated, setAccumulated] = useState<string[]>(['time']);

  let SEPARATED_OPTIONS = useSelectArrObj(filterVal);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'time',
    text: formatMessage({ id: 'common_by_Time' }),
    disabled: dateDisabled,
  });

  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns } = useTableColumns();

  const compData = useData(filterVal, filterLoading, selectType, config, otherParams);
  const {
    loading, isError, paging, setPaging, sort, setSort,
    total, tableData, statisticsData, filterValueSort, accumulateOldValueRef,
    getRequestParams, getData, downLoadData, frontLogicalFlag, overLimitBarTriggerKey
  } = compData || {};

  useEffect(() => {
    const columns = [
      DATE_COLUMN,
      ...getAccumulateColumns(accumulateOldValueRef?.current),
      ...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType)
    ];
    setColumns(columns);
  }, [accumulateOldValueRef?.current, selectType, filterVal.timeType, showMetrics, locale]);

  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterVal.timeType),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic, false)
  ];

  // 下载弹窗指标options
  const metricOptions: Option[] = useMemo(() => {
    return Object.keys(COLUMN_CONFIG).map((key) => {
      const config = COLUMN_CONFIG[key]?.metricsConfig;
      const name = isEn ? dynamicString(config?.enName, data) : dynamicString(config?.cnName, data);
      return ({ value: key, text: name });
    });
  }, [COLUMN_CONFIG, isEn]);

  const onSortChange = (sorts: SortBy[] = []) => {
    // 排序
    setSort(sorts);
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sorts, filterValueSort?.current?.filterValue, otherParams);
    // forceUpdate()
    getData(params);
  };

  const getExtractMetrics = (configMetrisArr: ComponentMetricsItemType[], metricsArr: any[]) => {
    return configMetrisArr.filter(item => metricsArr.includes(item.reqMetricKey));
  };

  const downloadConfig = {
    defaultValue: showMetrics,
    onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat }) => {
      const metricNameArr = getExtractMetrics(componentMetrics || [], metrics);
      // // 后续下载要给后端传递对应指标的中英文名称，此处提前处理，先注释掉
      const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
      metricNameArr?.forEach(item => {
        metricsMap[item?.reqMetricKey] = {
          en: dynamicString(item?.enName, data) || item?.enName || '',
          zh: dynamicString(item?.cnName, data) || item?.cnName || '',
          type: item?.type
        };
      });
      const params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
        metrics,
        metrics_map: metricsMap,
        show_dod: dataType === "show_dod",
        show_sdlw: dataType === "show_sdlw",
        file_format: fileFormat,
        aggregates: isDownloadStatistics ? showStatistic : []
      });
      const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
      return downLoadData(api, params);
    },
    timeType: filterVal.timeType,
    metricOptions,
    showData: { valueOnly: true, showDod: true, showSdlw: true }
  };

  const { setNoticeStatus } = useNotificationOfDownloadTable(title);
  function downPromise() {
    const metricNameArr = getExtractMetrics(componentMetrics || [], showMetrics);
    const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
    metricNameArr?.forEach(item => {
      metricsMap[item?.reqMetricKey] = {
        en: dynamicString(item?.enName, data) || item?.enName || '',
        zh: dynamicString(item?.cnName, data) || item?.cnName || '',
        type: item?.type
      };
    });
    const params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
      metrics: showMetrics,
      metrics_map: metricsMap,
      show_dod: selectType === "dod",
      show_sdlw: selectType === "sdlw",
      show_change: selectType === "change",
      // file_format: fileFormat,
      aggregates: showStatistic
    });
    const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
    return downLoadData(api, params);
  }
  // 下载
  function handleDownload() {
    const downloadPromise = downPromise?.();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${title?.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }

  return (
    <div className={className || ''} style={style || {}}>
      <PerformanceMetricsTable
        title={title}
        columns={columns}
        records={records}
        copyConfig={
          {
            copyTitleList: ['copy time','current','copy current and Datetime'],
            copyData: records
          }
        }
        selectType={selectType}
        timeType={filterVal?.timeType}
        sorting={{
          defaultSorts: sort,
          onSortChange,

        }}
        paging={{
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          recordCount: total,
          pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
          onPagingChange(query: PagingQuery) {
            // 切换分页
            setPaging(query);
            // 发起请求
            const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue, otherParams);
            getData(params);
          }
        }}
        topTip={{
          left: (<div className={`overall-conext-margin js-overview-table-dimension`}>
            <DimensionAccumulateRow
              options={SEPARATED_OPTIONS}
              value={accumulated}
              onChange={(value: string[]) => {
                setAccumulated(value);
                setPaging({ pageSize: 10, pageIndex: 1 });
                setSort([]);
                if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                  accumulateOldValueRef.current = value;
                  const params = getRequestParams({ pageSize: 10, pageIndex: 1 }, [], filterValueSort?.current?.filterValue);
                  getData(params);
                }
              }}
            />
            {isShowValueType && <SelectShowValueType
              timeType={filterVal.timeType}
              value={selectType}
              onChange={(value) => { setSelectType(value as ShowValueType) }}
              isShowDod={isShowDod}
              isShowSdlw={isShowSdlw}
              isShowChange={isShowChange}
            />}
          </div>),
          right: (<>
            {
              isShowMetricsSettings && <MetricsSettings
                onConfirm={({ metricValues, statisticsType }: MetricsSettingsProp) => {
                  setShowStatistic(statisticsType);
                  setShowMetrics(metricValues);
                }}
                disabledOptions={[{ value: "time", text: formatMessage({ id: "common_time" }) }]}
                metricOptions={metricOptions}
              />
            }
          </>)
        }}
        onResizeEnd={(columns) => setColumns(columns)}
        isShowProgressBar={isShowProgressBar}
        loading={loading}
        isError={isError}
        downloadConfig={isShowdownloadConfig ? downloadConfig : null}
        onDownload={handleDownload}
        errorText={formatMessage({ id: 'common_loading_failed_realtime'})}
        frontLogicalFlag={frontLogicalFlag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
      />
    </div>
  );
}
